import { useQuery } from '@tanstack/react-query';
import { apiClient } from './client';

export interface LineItemCostResult {
  box_material_cost: number;
  door_material_cost: number;
  back_material_cost: number;
  shelf_material_cost: number;
  edgeband_cost: number;
  stretcher_cost: number;
  scribe_cost: number;
  drawer_box_cost: number;
  hardware_cost: number;
  finish_cost: number;
  total_material_cost: number | null;
  total_cost_before_margin: number;
  framing_material_cost: number;
  finish_material_cost: number;
  finish_sqft: number;
  material_details: Array<{ name: string; category: string; sqft?: number; linft?: number; yards?: number; cost: number }>;
  hardware_details: Array<{ name: string; qty: number; cost_per: number; cost: number }>;
  calculation_deferred?: boolean;
  finish_name: string | null;
  finish_rate_per_sqft: number | null;
  price: number;
  margin_breakdown: Array<{
    category: string;
    cost: number;
    margin_rate: number;
    margin_amount: number;
  }>;
  material_cost_source?: 'nesting' | 'pending';
  labor_details: Array<{
    rate_name: string;
    rate_id: string;
    category: string;
    unit: string;
    units: number;
    rate_per_unit: number;
    cost: number;
    /** Per-weight contribution rows for per-hour rates. Each row carries
     *  `minutes` (already filtered to > 0 by the server) and the dollar
     *  `cost` already incorporating per-section vs total scaling. Omitted
     *  for fixed-pricing rates. Mirrors server `LaborItemDetail.score_breakdown`. */
    score_breakdown?: Array<{
      label: string;
      minutes: number;
      cost: number;
      basis: 'per_section' | 'total';
    }>;
  }>;
  labor_cost: number;
  exclude_labor: boolean;
  cnc_cost: number;
  nesting_panel_types?: string[];
}

export interface AreaCostResult {
  area_id: string;
  area_name: string;
  material_cost: number | null;
  framing_material_cost: number;
  finish_material_cost: number;
  hardware_cost: number;
  finish_cost: number;
  custom_charges_cost: number;
  toe_kick_cost: number;
  labor_cost: number;
  total_cost: number;
  line_items: Array<LineItemCostResult & { item_id: string; description: string; quantity: number; cabinet_code: string }>;
  total_cabinets: number;
  total_doors: number;
  total_drawers: number;
  total_shelves: number;
  material_cost_source?: 'nesting' | 'pending';
}

export interface ProjectCostResult {
  project_id: string;
  project_name: string;
  areas: AreaCostResult[];
  total_material_cost: number | null;
  total_framing_material_cost: number;
  total_finish_material_cost: number;
  total_hardware_cost: number;
  total_finish_cost: number;
  total_custom_charges: number;
  total_toe_kick_cost: number;
  total_labor_cost: number;
  subtotal_cost: number;
  framing_material_margin_amount: number;
  finish_material_margin_amount: number;
  hardware_margin_amount: number;
  labor_margin_amount: number;
  finish_margin_amount: number;
  cnc_margin_amount: number;
  overall_margin_amount: number;
  total_margin: number;
  grand_total: number;
  total_cabinets: number;
  total_doors: number;
  total_drawers: number;
  total_shelves: number;
  labor_details: Array<{
    rate_name: string;
    rate_id: string;
    category: string;
    unit: string;
    rate_per_unit: number;
    total_units: number;
    total_cost: number;
  }>;
  labor_per_area: Record<string, number>;
  labor_per_item: Record<string, number>;
  margin_profile_name: string;
  margin_rates: {
    framing_material: number;
    finish_material: number;
    hardware: number;
    labor: number;
    finish: number;
    overall: number;
    cnc: number;
  };
  material_cost_source?: 'nesting' | 'pending';
}

export function useLineItemCost(lineItemId?: string) {
  return useQuery({
    queryKey: ['calculations', 'line-item', lineItemId],
    queryFn: async () => {
      const { data } = await apiClient.get<LineItemCostResult>(
        `/calculations/line-item/${lineItemId}/cost`,
      );
      return data;
    },
    enabled: !!lineItemId,
  });
}

export function useAreaCost(areaId?: string, projectId?: string) {
  return useQuery({
    queryKey: ['calculations', 'area', areaId],
    queryFn: async () => {
      const { data } = await apiClient.get<AreaCostResult>(
        `/calculations/area/${areaId}/cost`,
        { params: { projectId } },
      );
      return data;
    },
    enabled: !!areaId && !!projectId,
  });
}

export function useProjectCost(projectId?: string) {
  return useQuery({
    queryKey: ['calculations', 'project', projectId],
    queryFn: async () => {
      const { data } = await apiClient.get<ProjectCostResult>(
        `/calculations/project/${projectId}/cost`,
      );
      return data;
    },
    enabled: !!projectId,
  });
}
